// The swapped-session STORE — who this session is currently acting as, and the bearer it started with.
//
// Shared infrastructure for the DEV/LOCAL fixture switcher (`app/devFixtureSwitch.ts`), and deliberately
// kept in its own module: `useAuth` clears it on logout and the switcher button reads it, neither of which
// should have to import the switcher's fetch/scorch machinery.
//
// It once served a second consumer, the band-seat swap (a band owner signing in as their own crew), removed
// 2026-08-31 when the fixture switcher subsumed it — band members are fixture accounts too, and the fixture
// switcher additionally makes the hop the band one structurally could not (a band seat to an org one).
//
// Two properties make a swap safe to hand to a tester, and both belong to the SESSION rather than to this
// store:
//   • the swapped bearer is MEMORY-ONLY — the server withholds the re-issue token for an impersonated
//     session, so a browser RELOAD resolves the caller's untouched httpOnly cookie and lands back on them.
//     Never a way to persist as someone else;
//   • the caller's own bearer is held HERE, in memory, exactly where WebTokenProvider already holds it —
//     never `localStorage` (the lint-enforced rule), never a cookie, never disk.
import type { TokenProvider } from "@cntrl/native";
import { create } from "zustand";

/** Who the app is currently acting as, for the switcher's glow and the return path. */
export interface ActingAs {
  personId: string;
  displayName: string;
  email: string;
  /** The swap session's id. Recorded for the log; there is no revoke route (see devFixtureSwitch). */
  sessionId: string;
}

interface DevImpersonationState {
  actingAs: ActingAs | null;
  /** The bearer this session STARTED as, held for the return trip. In memory only. */
  ownBearer: string | null;
  begin: (actingAs: ActingAs, ownBearer: string | null) => void;
  end: () => void;
}

/**
 * Deliberately NOT registered with the store-reset registry, for exactly activeBand's reason: a switch
 * scorches every registered store and this must SURVIVE that scorch — it is the identity the app is now
 * running as, not stale tenant data. `useAuth().logout()` clears it explicitly, alongside activeBand.
 */
export const useDevImpersonation = create<DevImpersonationState>((set) => ({
  actingAs: null,
  ownBearer: null,
  begin: (actingAs, ownBearer) => set({ actingAs, ownBearer }),
  end: () => set({ actingAs: null, ownBearer: null }),
}));

/**
 * The bearer to authorize a dev-swap call with: the one this session STARTED as, falling back to the current
 * one before any swap has happened.
 *
 * This is what makes hopping seat-to-seat work. It mattered absolutely for the removed band swap, whose
 * routes were owner-only — while wearing a member's seat the current bearer would have been refused by all
 * three of them. It matters less for the fixture switcher (every fixture account may call its route), but the
 * rule is kept: a chain of hops is authorized by whoever began it, not by the seat you happen to be wearing.
 */
export function swapAuthority(tokenProvider: TokenProvider): string | null {
  return useDevImpersonation.getState().ownBearer ?? tokenProvider.getBearer();
}
